








































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  AppointmentType,
  AppointmentTypeKind,
  InputAppointmentType,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import { AvailabilityIntervalType } from "@scrile/api-provider/dist/projects/scrileConnect/UserAvailabilityProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { Tab } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import AppointmentTypeItemSettings from "./components/AppointmentTypeItemSettings";

export default defineComponent({
  name: "AppointmentTypesSettings",
  components: { BaseSegmentedButton, BaseButton, AppointmentTypeItemSettings },
  props: {
    manualSave: { type: Boolean },
    activeType: { type: String as PropType<AvailabilityIntervalType>, required: true },
  },
  setup(props, { emit }) {
    const isOfflineSettings = computed(() => props.activeType === AvailabilityIntervalType.TOUR_GUIDE);

    const { authUserStrict } = useAuthUser();
    const appointmentKinds: Tab[] = [
      {
        text: $t("Chat"),
        value: AppointmentTypeKind.MESSAGING,
      },
      {
        text: $t("Video"),
        value: AppointmentTypeKind.VIDEO_CALLS,
      },
      {
        text: $t("Audio"),
        value: AppointmentTypeKind.AUDIO_CALLS,
      },
    ];

    const currentKind = ref<AppointmentTypeKind>(
      isOfflineSettings.value ? AppointmentTypeKind.TOUR_GUIDE : AppointmentTypeKind.MESSAGING
    );

    watch(isOfflineSettings, () => {
      currentKind.value = isOfflineSettings.value ? AppointmentTypeKind.TOUR_GUIDE : AppointmentTypeKind.MESSAGING;
    });

    const paymentPackages = ref<PaymentPackage[]>([]);
    const fetchPaymentPackages = async () => {
      paymentPackages.value = await providers.PaymentPackageProvider.findAll({
        type: [PaymentPackageEnum.APPOINTMENT],
      });
    };

    const appointments = ref<AppointmentType[]>([]);
    const fetchTypes = async () => {
      const result = await providers.AppointmentTypeProvider.search({
        data: { userIds: [authUserStrict.value.id] },
      });
      appointments.value = result.find(i => i.userId === authUserStrict.value.id)?.types ?? [];
      emit("setValid", !!appointments.value.length);
    };

    const updateTypes = throttle(async (types: AppointmentType[]) => {
      const result = await providers.AppointmentTypeProvider.update({
        data: {
          userId: authUserStrict.value.id,
          types: types.map<InputAppointmentType>(t => ({
            kind: t.kind,
            duration: t.duration,
            pricePackageId: t.pricePackageId,
          })),
        },
      });
      appointments.value = result.types;
    }, 500);

    const manualUpdateTypes = (types: AppointmentType[]) => {
      emit("setTypes", types);
      emit("setValid", !!types.length);
    };

    const groupedAppointments = computed(() => {
      const result: Record<AppointmentTypeKind, AppointmentType[]> = {
        [AppointmentTypeKind.MESSAGING]: [],
        [AppointmentTypeKind.VIDEO_CALLS]: [],
        [AppointmentTypeKind.AUDIO_CALLS]: [],
        [AppointmentTypeKind.TOUR_GUIDE]: [],
      };
      appointments.value.forEach(appointment => {
        result[appointment.kind].push(appointment);
      });
      return result;
    });

    const currenAppointments = computed(() => {
      return groupedAppointments.value[currentKind.value];
    });

    const selectedDurations = computed(() => currenAppointments.value.map(i => i.duration));

    const showAddPriceButton = ref(true);
    const onEmptyDuration = (status: boolean) => {
      showAddPriceButton.value = !status;
    };
    watch(currentKind, () => {
      showAddPriceButton.value = true;
    });

    const onAddType = async () => {
      appointments.value.push({
        id: "-" + appointments.value.length,
        duration: 0,
        pricePackageId: paymentPackages.value[0]?.id,
        kind: currentKind.value,
      });
    };

    const onUpdate = (data: { id: AppointmentType["id"]; duration: number; pricePackageId: PaymentPackage["id"] }) => {
      appointments.value = appointments.value.map(a => {
        if (a.id !== data.id) {
          return a;
        }
        return {
          ...a,
          duration: data.duration,
          pricePackageId: data.pricePackageId,
        };
      });
      props.manualSave ? manualUpdateTypes(appointments.value) : updateTypes(appointments.value);
    };

    const onRemove = (id: AppointmentType["id"]) => {
      appointments.value = appointments.value.filter(a => a.id !== id);
      props.manualSave ? manualUpdateTypes(appointments.value) : updateTypes(appointments.value);
    };

    const init = () => {
      emit("setValid", false);
      emit("setCb", (data: AppointmentType[]) => updateTypes(data));
      Promise.all([fetchTypes(), fetchPaymentPackages()]);
    };

    init();

    return {
      appointmentKinds,
      currentKind,
      currenAppointments,
      selectedDurations,
      isOfflineSettings,
      onAddType,
      paymentPackages,
      onRemove,
      onUpdate,
      showAddPriceButton,
      onEmptyDuration,
    };
  },
});
